/**
 * Multi-select campaign picker for Flow 2 (Create Ad Groups).
 * Portal-rendered via the shared `Popover` so it overlays any
 * cs-block / cs-foot regardless of stacking context.
 *
 * Source data: the mirror via `useCampaignsList(appId)`. Selection
 * is a `Set<number>` lifted to the parent; the picker just shows
 * checkboxes + Select all / Clear all.
 */

import { useMemo, useRef, useState } from "react";
import { I } from "@/components/Icon";
import { useCampaignsList, useReadOnlyCampaigns } from "@/lib/api-hooks";
import { Popover } from "./Popover";

export function CampaignMultiPicker({
  appId,
  selected,
  onChange,
  disabled,
}: {
  appId: number | null;
  selected: Set<number>;
  onChange: (next: Set<number>) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const q_ = useCampaignsList(appId);
  // Кампании групп Apple только на чтение выбрать нельзя (ТЗ §10).
  const readOnly = useReadOnlyCampaigns().data;
  const RO_HINT = "Read-only access — reconnect with Read & Write to enable changes.";

  const filtered = useMemo(() => {
    const rows = q_.data ?? [];
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) =>
      (r.name ?? "").toLowerCase().includes(needle),
    );
  }, [q_.data, q]);

  const toggle = (id: number) => {
    if (readOnly?.has(id)) return;
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(next);
  };

  const selectAll = () => {
    onChange(
      new Set(
        (q_.data ?? [])
          .map((r) => r.campaign_id)
          .filter((id) => !readOnly?.has(id)),
      ),
    );
  };
  const clearAll = () => onChange(new Set());

  const triggerLabel = (() => {
    if (!appId) return "Pick an app first";
    if (q_.isLoading) return "Loading campaigns…";
    if (selected.size === 0) return "Select target campaigns…";
    if (selected.size === 1) {
      const row = (q_.data ?? []).find(
        (r) => r.campaign_id === [...selected][0],
      );
      return row?.name ?? `1 campaign selected`;
    }
    return `${selected.size} campaigns selected`;
  })();

  return (
    <div className="cs-combobox">
      <button
        ref={triggerRef}
        type="button"
        className="cs-combo-trigger"
        data-open={open ? "true" : "false"}
        disabled={disabled || !appId}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="cs-combo-trig-placeholder">{triggerLabel}</span>
        <span className="cs-combo-trig-chev">{I.chevronDown}</span>
      </button>
      <Popover
        open={open}
        anchorRef={triggerRef}
        onClose={() => setOpen(false)}
      >
        <div className="cs-combo-pop" style={{ position: "static" }}>
          <div className="cs-combo-search">
            {I.search}
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Filter by name…"
            />
          </div>
          <div
            style={{
              display: "flex",
              gap: 6,
              padding: "4px 10px",
              borderBottom: "1px solid var(--c-divider)",
              fontSize: 12,
            }}
          >
            <button
              type="button"
              onClick={selectAll}
              style={{
                background: "transparent",
                border: 0,
                cursor: "pointer",
                color: "var(--c-accent-600)",
              }}
            >
              Select all
            </button>
            <span style={{ color: "var(--c-text-4)" }}>·</span>
            <button
              type="button"
              onClick={clearAll}
              style={{
                background: "transparent",
                border: 0,
                cursor: "pointer",
                color: "var(--c-text-3)",
              }}
            >
              Clear
            </button>
          </div>
          {filtered.map((row) => {
            const checked = selected.has(row.campaign_id);
            const ro = !!readOnly?.has(row.campaign_id);
            return (
              <div
                key={row.campaign_id}
                className="cs-combo-opt"
                data-active={checked ? "true" : "false"}
                onClick={() => toggle(row.campaign_id)}
                title={ro ? RO_HINT : undefined}
                style={ro ? { opacity: 0.5, cursor: "default" } : undefined}
              >
                <span
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: 5,
                    border: "1.5px solid var(--c-border-strong)",
                    display: "grid",
                    placeItems: "center",
                    background: checked
                      ? "var(--c-accent-600)"
                      : "var(--c-surface)",
                    borderColor: checked
                      ? "var(--c-accent-600)"
                      : "var(--c-border-strong)",
                    flex: "none",
                  }}
                >
                  {checked && (
                    <svg
                      width="11"
                      height="11"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#fff"
                      strokeWidth="3.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </span>
                <span className="cs-combo-opt-text">
                  <div className="cs-combo-opt-name">{row.name ?? "—"}</div>
                  <div className="cs-combo-opt-sub">
                    id {row.campaign_id} ·{" "}
                    {(row.status ?? "—").toLowerCase()}
                    {ro ? " · read-only access" : ""}
                  </div>
                </span>
              </div>
            );
          })}
          {!filtered.length && (
            <div
              style={{
                padding: "24px 14px",
                textAlign: "center",
                fontSize: 13,
                color: "var(--c-text-3)",
              }}
            >
              {appId
                ? `No matches for "${q}"`
                : "Pick an app to see its campaigns"}
            </div>
          )}
        </div>
      </Popover>
    </div>
  );
}
